import i18n from "i18n";
import { ImagePaywallDesignCanvas, ImagePaywallTechPack, ImagePaywallTraining, ImagePaywallTrendAnalysis, ImagePhotoshootStudioLanding } from "images";
import { IPackageItem, IPackageList } from "api/payment/payment.type";
import { PackageType, PaywallContent, PopupReason, ScreenType } from "./WarningSnackbar.types";

export const getText = (type: any): string => {
  switch (type) {
    case 402:
      return i18n.t("snackbar:402.default");
    case 409:
      return i18n.t("snackbar:409.default");
    case 503:
      return i18n.t("snackbar:503.default");
    default:
      return i18n.t("snackbar:generic.default");
  }
};

export const getScreenTypeText = (screen: ScreenType): string => {
  switch (screen) {
    case ScreenType.Train:
      return i18n.t("snackbar:paywall.feature_training");
    case ScreenType.Team:
      return i18n.t("snackbar:paywall.feature_team");
    case ScreenType.Tryon:
      return i18n.t("snackbar:paywall.feature_tryon");
    case ScreenType.Design:
      return i18n.t("snackbar:paywall.feature_design");
    case ScreenType.Mannequin:
      return i18n.t("snackbar:paywall.feature_mannequin");
    case ScreenType.Ghost:
      return i18n.t("snackbar:paywall.feature_ghost");
    case ScreenType.Marketplace:
      return i18n.t("snackbar:paywall.feature_marketplace");
    case ScreenType.TechPack:
      return i18n.t("snackbar:paywall.feature_tech_pack");
    case ScreenType.TrendAnalysis:
      return i18n.t("snackbar:paywall.feature_trend_analysis");
    case ScreenType.StyleTraining:
      return i18n.t("snackbar:paywall.feature_style_training");
    case ScreenType.ApiAccess:
      return i18n.t("snackbar:paywall.feature_api_access");
    default:
      return "";
  }
};

export const isDemoUserBlockedForScreen = (
  demoInfo: { is_design: boolean; is_mannequin: boolean; is_video: boolean; is_techpack: boolean; is_trend: boolean } | undefined,
  screen: ScreenType | undefined
): boolean => {
  if (!demoInfo || screen === undefined || Object.keys(demoInfo).length === 0) return false;

  switch (screen) {
    case ScreenType.Design:
    case ScreenType.Edit:
      return !demoInfo.is_design;
    case ScreenType.Mannequin:
    case ScreenType.Tryon:
    case ScreenType.Ghost:
      return !demoInfo.is_mannequin;
    default:
      return false;
  }
};

export const getPackageTypeFromPopupType = (popupType: string): PackageType | null => {
  switch (popupType.toLowerCase()) {
    case "design_starter":
      return PackageType.DesignStarter;
    case "photoshoot_starter":
      return PackageType.PhotoshootStarter;
    case "design_pro":
      return PackageType.DesignPro;
    case "design_studio":
      return PackageType.DesignStudio;
    case "creative_suite":
      return PackageType.CreativeSuite;
    default:
      return null;
  }
};

const packageTypeToName: Record<PackageType, string> = {
  [PackageType.DesignStarter]: "Design Starter",
  [PackageType.PhotoshootStarter]: "Photoshoot Starter",
  [PackageType.DesignPro]: "Design Pro",
  [PackageType.DesignStudio]: "Design Studio",
  [PackageType.CreativeSuite]: "Creative Suite",
};

const findPackageFromList = (packageList: IPackageList | null, targetPackage: PackageType): IPackageItem | null => {
  if (!packageList) return null;

  const targetName = packageTypeToName[targetPackage]?.toLowerCase();
  const allPackages = [...(packageList.monthly || []), ...(packageList.yearly || [])];

  return allPackages.find((pkg) => pkg.name.toLowerCase() === targetName) || null;
};

const buildPackageConfig = (pkg: IPackageItem | null, targetPackage: PackageType) => {
  const name = packageTypeToName[targetPackage] || "Design Studio";
  const title = i18n.t("snackbar:paywall.title_upgrade", { name });
  const subtitle = i18n.t("snackbar:paywall.subtitle_when_upgrade", { name });

  if (!pkg) {
    return { title, subtitle, items: [] };
  }

  const items = pkg.features.map((feature) => ({ value: feature, text: "" }));

  return { title, subtitle, items };
};

// Resolve descriptions lazily via i18n.t so callers pick up the current locale.
// Keys live in snackbar:paywall.description_*. BE-driven `pkg.features` bullet
// items still render in the BE locale (separate localization concern).
const getDefaultDescription = (): string => i18n.t("snackbar:paywall.description_default");

const getScreenDescription = (screen: ScreenType): string | undefined => {
  const keyByScreen: Partial<Record<ScreenType, string>> = {
    [ScreenType.Design]: "snackbar:paywall.description_design",
    [ScreenType.Team]: "snackbar:paywall.description_team",
    [ScreenType.Tryon]: "snackbar:paywall.description_tryon",
    [ScreenType.Mannequin]: "snackbar:paywall.description_mannequin",
    [ScreenType.Ghost]: "snackbar:paywall.description_ghost",
    [ScreenType.Marketplace]: "snackbar:paywall.description_marketplace",
    [ScreenType.TechPack]: "snackbar:paywall.description_tech_pack",
    [ScreenType.TrendAnalysis]: "snackbar:paywall.description_trend_analysis",
    [ScreenType.ApiAccess]: "snackbar:paywall.description_api_access",
  };
  const key = keyByScreen[screen];

  return key ? i18n.t(key) : undefined;
};

/** A paywall picture: its backoffice "Site Görselleri" slot and the bundled file shown when none is published. */
export interface PaywallImage {
  slotKey: string;
  src: string;
}

const PAYWALL_DESIGN_CANVAS: PaywallImage = { slotKey: "campaign.paywall_design_canvas", src: ImagePaywallDesignCanvas };
const PAYWALL_TRAINING: PaywallImage = { slotKey: "campaign.paywall_training", src: ImagePaywallTraining };
const PAYWALL_TREND_ANALYSIS: PaywallImage = { slotKey: "campaign.paywall_trend_analysis", src: ImagePaywallTrendAnalysis };
const PAYWALL_TECH_PACK: PaywallImage = { slotKey: "campaign.paywall_techpack", src: ImagePaywallTechPack };
const PAYWALL_PHOTOSHOOT: PaywallImage = { slotKey: "campaign.photoshoot_studio_landing", src: ImagePhotoshootStudioLanding };

const screenImages: Partial<Record<ScreenType, PaywallImage>> = {
  [ScreenType.Design]: PAYWALL_DESIGN_CANVAS,
  [ScreenType.Train]: PAYWALL_TRAINING,
  [ScreenType.Team]: PAYWALL_TREND_ANALYSIS,
  [ScreenType.Tryon]: PAYWALL_PHOTOSHOOT,
  [ScreenType.Mannequin]: PAYWALL_PHOTOSHOOT,
  [ScreenType.Ghost]: PAYWALL_PHOTOSHOOT,
  [ScreenType.Marketplace]: PAYWALL_PHOTOSHOOT,
  [ScreenType.TechPack]: PAYWALL_TECH_PACK,
  [ScreenType.TrendAnalysis]: PAYWALL_TREND_ANALYSIS,
  [ScreenType.StyleTraining]: PAYWALL_TRAINING,
};

const defaultPaywallImage = PAYWALL_TREND_ANALYSIS;

/** The paywall picture for `screen`. `getPaywallContentForTargetPackage` returns its bundled `src`; the React caller resolves the slot. */
export const getPaywallImage = (screen: ScreenType): PaywallImage => screenImages[screen] ?? defaultPaywallImage;

// Screens whose popup names no package: which plans grant API access is not one of the packages the
// paywall upsells (K98), so its popup carries no "When you upgrade to …, you also get:" section.
const screensWithoutPackageSection: ScreenType[] = [ScreenType.ApiAccess];

/**
 * P7 Faz 1: the paywall for a 402 whose `popup_type` names no package this build knows — a new, renamed or custom plan,
 * or none at all. It used to render NOTHING (the action just failed). Plan-neutral copy (never the "Upgrade to Design
 * Studio" descriptions: the plan is unknown) and the usual Upgrade → /pricing.
 */
export const getGenericPaywallContent = (screen: ScreenType, popupReason?: PopupReason): PaywallContent => ({
  title: i18n.t("snackbar:paywall.title_to_access", { feature: getScreenTypeText(screen) || i18n.t("snackbar:paywall.feature_fallback") }),
  description: popupReason === "credits" ? i18n.t("snackbar:paywall.description_credits") : i18n.t("snackbar:402.default"),
  subtitle: "",
  items: [],
  img: getPaywallImage(screen).src,
});

export const getPaywallContentForTargetPackage = (targetPackage: PackageType, screen: ScreenType, packageList?: IPackageList | null, popupReason?: PopupReason): PaywallContent => {
  const pkg = findPackageFromList(packageList || null, targetPackage);
  const config = screensWithoutPackageSection.includes(screen) ? { ...buildPackageConfig(pkg, targetPackage), subtitle: "", items: [] } : buildPackageConfig(pkg, targetPackage);
  const img = getPaywallImage(screen).src;

  if (popupReason === "credits") {
    return {
      ...config,
      description: i18n.t("snackbar:paywall.description_credits"),
      img,
    };
  }

  if (popupReason === "permissions") {
    const featureName = getScreenTypeText(screen) || i18n.t("snackbar:paywall.feature_fallback");

    return {
      ...config,
      title: i18n.t("snackbar:paywall.title_to_access", { feature: featureName }),
      description: getScreenDescription(screen) ?? getDefaultDescription(),
      img,
    };
  }

  const description = getScreenDescription(screen) ?? getDefaultDescription();

  return { ...config, description, img };
};
